import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FilmStrip, Play, DownloadSimple, ShareNetwork, Sparkle, MusicNote, Check, Upload, Sliders, ArrowsClockwise, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGE_LABELS = {
  queued: 'Queued',
  building_timeline: 'Normalising clips',
  concat: 'Joining clips',
  mixing_music: 'Mixing music',
  uploading: 'Saving your film',
  complete: 'Complete',
};

const FilmModule = ({ event, mediaCount }) => {
  const navigate = useNavigate();
  const [filmStatus, setFilmStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [musicStyle, setMusicStyle] = useState('romantic');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [photoDuration, setPhotoDuration] = useState(3.0);
  const [maxClips, setMaxClips] = useState(50);
  const [customMusicName, setCustomMusicName] = useState(event?.film?.custom_music_path ? 'Custom track uploaded' : '');
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const [library, setLibrary] = useState([]);
  const [libraryPickId, setLibraryPickId] = useState('');
  const [shareModal, setShareModal] = useState(null);
  const [creatingShare, setCreatingShare] = useState(false);
  const videoRef = useRef(null);
  const musicFileRef = useRef(null);

  const filmPurchased = event.film_purchased || event.package_type === 'premium';

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.event_id]);

  useEffect(() => {
    axios.get(`${API}/music/library`).then(r => setLibrary(r.data || [])).catch(() => {});
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/events/${event.event_id}/film/status`);
      setFilmStatus(res.data);
    } catch (e) { /* ignore */ }
  };

  const authHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      const res = await axios.post(`${API}/events/${event.event_id}/film/purchase`, {});
      if (res.data.url) window.location.href = res.data.url;
      else toast.error('Failed to create checkout');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  const handleMusicUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('Music file must be under 25 MB');
      return;
    }
    setUploadingMusic(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/events/${event.event_id}/film/music`, fd, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      setCustomMusicName(file.name);
      toast.success('Music uploaded — used on next generation');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Music upload failed');
    } finally {
      setUploadingMusic(false);
      if (musicFileRef.current) musicFileRef.current.value = '';
    }
  };

  const handleMusicRemove = async () => {
    try {
      await axios.delete(`${API}/events/${event.event_id}/film/music`, { headers: authHeaders() });
      setCustomMusicName('');
      toast.success('Custom music removed — will use style default');
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const handleGenerate = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      toast.error('Please log in to generate your film');
      navigate('/login');
      return;
    }
    setGenerating(true);
    try {
      await axios.post(`${API}/events/${event.event_id}/film/generate`, {
        music_style: musicStyle,
        photo_duration: photoDuration,
        max_clips: maxClips,
        music_library_id: libraryPickId || null,
      }, { headers: authHeaders() });
      toast.success('Film generation started!');
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start');
    } finally {
      setGenerating(false);
    }
  };

  const getVideoUrl = () => {
    const url = filmStatus?.video_url;
    if (!url) return '';
    return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
  };

  const handleDownload = async () => {
    try {
      const url = `${API}/events/${event.event_id}/film/stream`;
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(event.event_name || 'event').replace(/\s/g, '_')}_film.mp4`;
      link.click();
      toast.success('Downloading film...');
    } catch {
      toast.error('Download failed');
    }
  };

  const handleCreateShare = async () => {
    setCreatingShare(true);
    try {
      const res = await axios.post(`${API}/events/${event.event_id}/share`, {}, { headers: authHeaders() });
      const url = `${window.location.origin}/share/${res.data.share_token}`;
      setShareModal(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create share link');
    } finally {
      setCreatingShare(false);
    }
  };

  const copyShareUrl = () => {
    if (!shareModal) return;
    navigator.clipboard?.writeText(shareModal);
    toast.success('Share link copied to clipboard');
  };

  const status = filmStatus?.status || event.film?.status || 'none';

  // ── Share modal ──
  const shareModalEl = shareModal && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShareModal(null)} data-testid="share-modal">
      <div className="bg-[#141416] rounded-2xl p-6 max-w-md w-full border border-white/10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-bold text-white flex items-center gap-2"><ShareNetwork weight="bold" /> Share with guests</h4>
          <button onClick={() => setShareModal(null)} className="text-gray-400 hover:text-white" data-testid="close-share-modal"><X size={20} /></button>
        </div>
        <p className="text-sm text-gray-400 mb-4">Anyone with this link can view the gallery, film & highlight. No account needed.</p>
        <div className="flex gap-2 mb-3">
          <input value={shareModal} readOnly className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono" data-testid="share-url-input" />
          <button onClick={copyShareUrl} className="px-4 py-2 rounded-lg bg-gradient-to-br from-[#B89762] to-[#B89762] text-white font-semibold text-sm" data-testid="copy-share-url">Copy</button>
        </div>
        <p className="text-xs text-gray-600">Click Copy to share via text, WhatsApp, email, etc.</p>
      </div>
    </div>
  );

  // ── STATE 1: NOT PURCHASED ──
  if (!filmPurchased && status === 'none') {
    return (
      <div className="rounded-2xl mb-8 overflow-hidden" style={{ background: '#0F0F10' }} data-testid="film-module-purchase">
        <div className="p-8">
          <div className="flex items-center gap-2 mb-1">
            <FilmStrip size={24} weight="bold" className="text-white" />
            <h3 className="font-serif text-2xl font-bold text-white">Your Event Film</h3>
          </div>
          <p className="text-gray-300 text-lg mb-1 font-medium">Relive your entire event — through your guests' eyes</p>
          <p className="text-gray-500 text-sm mb-6">We automatically turn all your guest photos, videos and messages into one beautiful cinematic film. No editing. No effort. Just press create.</p>

          {mediaCount && (mediaCount.photos > 0 || mediaCount.videos > 0) && (
            <div className="mb-5 p-4 rounded-xl bg-[#15161B] border border-white/10" data-testid="film-media-counter">
              <p className="text-sm text-gray-400 mb-2">Your guests captured everything:</p>
              <p className="text-lg font-bold text-white">
                {mediaCount.photos > 0 && <span>{mediaCount.photos} photos</span>}
                {mediaCount.photos > 0 && mediaCount.videos > 0 && <span className="text-gray-500"> · </span>}
                {mediaCount.videos > 0 && <span>{mediaCount.videos} videos</span>}
              </p>
              <p className="text-sm text-gray-400 mt-1">Ready to turn this into your event film?</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 mb-5">
            {['All guest moments combined', 'Beautifully edited into one story', 'Ready in minutes', 'Share with family instantly'].map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-gray-300">
                <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #B89762, #A033D8)' }}>
                  <Check size={8} weight="bold" className="text-white" />
                </div>
                {f}
              </div>
            ))}
          </div>

          <button
            onClick={handlePurchase}
            disabled={purchasing}
            className="w-full py-4 rounded-xl font-bold text-white text-lg disabled:opacity-50 hover:scale-[1.02] transition-all"
            style={{ background: 'linear-gradient(135deg, #B89762 0%, #A033D8 50%, #C29D52 100%)' }}
            data-testid="purchase-film-btn"
          >
            {purchasing ? 'Redirecting...' : 'Create Our Event Film — £29'}
          </button>
          <p className="text-center text-gray-600 text-xs mt-3">Most couples create this the next morning</p>
        </div>
      </div>
    );
  }

  // ── STATE 2: PURCHASED, NOT GENERATED (or failed) ──
  if (status === 'none' || status === 'failed') {
    return (
      <div className="rounded-2xl mb-8 overflow-hidden" style={{ background: '#0F0F10' }} data-testid="film-module-create">
        <div className="p-8">
          <div className="flex items-center gap-2 mb-1">
            <FilmStrip size={24} weight="bold" className="text-white" />
            <h3 className="font-serif text-2xl font-bold text-white">Your Event Film</h3>
          </div>
          <p className="text-gray-300 text-lg mb-1 font-medium">Relive your entire event — through your guests' eyes</p>
          <p className="text-gray-500 text-sm mb-6">Choose your music and we'll combine all your guest footage into one beautiful film.</p>

          {status === 'failed' && (
            <div className="mb-4 p-3 rounded-lg bg-teal-500/10 border border-teal-500/20 text-teal-300 text-sm" data-testid="film-failed-banner">
              🎬 Your Photo Slideshow is ready to watch below. Tap <b>Create Our Event Film</b> below when you'd like to try your cinematic Film again.
              {filmStatus?.error && (
                <div className="mt-2 text-xs text-teal-300/70 font-mono break-all" data-testid="film-error-detail">
                  Last error: {filmStatus.error}
                </div>
              )}
            </div>
          )}

          {/* Music style */}
          <div className="mb-4">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Music Style</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'romantic', label: 'Romantic', desc: 'Warm & soft' },
                { id: 'cinematic', label: 'Cinematic', desc: 'Deep & epic' },
                { id: 'upbeat', label: 'Upbeat', desc: 'Bright & fun' },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMusicStyle(m.id)}
                  className={`p-3 rounded-xl border text-left transition-all ${musicStyle === m.id ? 'border-[#B89762]/50 bg-[#B89762]/10' : 'border-white/10 hover:border-white/20'}`}
                  data-testid={`music-${m.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <MusicNote size={14} weight="bold" className={musicStyle === m.id ? 'text-[#B89762]' : 'text-gray-500'} />
                    <span className={`text-sm font-semibold ${musicStyle === m.id ? 'text-white' : 'text-gray-300'}`}>{m.label}</span>
                  </div>
                  <p className="text-xs text-gray-500">{m.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Music Library picker */}
          {library.length > 0 && (
            <div className="mb-4 p-4 rounded-xl bg-[#15161B]/40 border border-white/5" data-testid="music-library-section">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Or pick from our library</span>
                {libraryPickId && (
                  <button onClick={() => setLibraryPickId('')} className="text-xs text-gray-400 hover:text-white" data-testid="clear-library-pick">Clear</button>
                )}
              </div>
              <select
                value={libraryPickId}
                onChange={(e) => setLibraryPickId(e.target.value)}
                disabled={!!customMusicName}
                className="w-full bg-[#15161B] border border-white/10 rounded-lg px-3 py-2 text-white text-sm disabled:opacity-40"
                data-testid="music-library-select"
              >
                <option value="">— Use the style default above —</option>
                {Object.entries(library.reduce((acc, t) => {
                  const g = t.mood || 'upbeat'; acc[g] = acc[g] || []; acc[g].push(t); return acc;
                }, {})).map(([mood, tracks]) => (
                  <optgroup key={mood} label={mood.charAt(0).toUpperCase() + mood.slice(1)}>
                    {tracks.map((t) => (
                      <option key={t.track_id} value={t.track_id}>
                        {t.name}{t.duration_sec ? ` · ${Math.round(t.duration_sec)}s` : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {libraryPickId && (() => {
                const t = library.find(x => x.track_id === libraryPickId);
                if (!t) return null;
                return (
                  <div className="mt-2 flex items-center gap-2" data-testid="library-preview">
                    <audio controls src={`${API}/music/library/${t.track_id}/stream`} className="w-full h-8" />
                  </div>
                );
              })()}
              {customMusicName && <p className="text-[10px] text-amber-400 mt-2">Your uploaded track below will override this pick.</p>}
            </div>
          )}

          {/* Custom music upload */}
          <div className="mb-4 p-4 rounded-xl bg-[#15161B]/40 border border-white/5" data-testid="custom-music-section">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Or use your own track</span>
              {customMusicName && (
                <button onClick={handleMusicRemove} className="text-xs text-red-400 hover:text-red-300" data-testid="remove-custom-music">Remove</button>
              )}
            </div>
            {customMusicName ? (
              <div className="flex items-center gap-2 text-sm text-green-400" data-testid="custom-music-present">
                <Check size={14} weight="bold" /> {customMusicName}
              </div>
            ) : (
              <>
                <input
                  ref={musicFileRef}
                  type="file"
                  accept="audio/mpeg,audio/mp3,audio/mp4,audio/wav,audio/ogg,audio/m4a,.mp3,.m4a,.wav,.ogg"
                  className="hidden"
                  onChange={handleMusicUpload}
                  data-testid="custom-music-input"
                />
                <button
                  onClick={() => musicFileRef.current?.click()}
                  disabled={uploadingMusic}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 hover:border-white/20 text-sm text-white disabled:opacity-50"
                  data-testid="custom-music-upload-btn"
                >
                  <Upload size={14} weight="bold" /> {uploadingMusic ? 'Uploading…' : 'Upload MP3 (max 25 MB)'}
                </button>
                <p className="text-xs text-gray-600 mt-2">Tip: use any song — your custom track overrides the style above.</p>
              </>
            )}
          </div>

          {/* Advanced clip controls */}
          <div className="mb-6">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-2 text-xs font-semibold text-gray-400 hover:text-white mb-3"
              data-testid="toggle-advanced"
            >
              <Sliders size={14} weight="bold" /> Advanced options {showAdvanced ? '▴' : '▾'}
            </button>
            {showAdvanced && (
              <div className="space-y-4 p-4 rounded-xl bg-[#15161B]/40 border border-white/5" data-testid="advanced-controls">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-gray-400">Photo duration</label>
                    <span className="text-xs font-mono text-white">{photoDuration.toFixed(1)}s</span>
                  </div>
                  <input
                    type="range" min="1.5" max="5" step="0.5"
                    value={photoDuration}
                    onChange={(e) => setPhotoDuration(parseFloat(e.target.value))}
                    className="w-full accent-[#B89762]"
                    data-testid="photo-duration-slider"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs text-gray-400">Max clips (shorter = punchier)</label>
                    <span className="text-xs font-mono text-white">{maxClips}</span>
                  </div>
                  <input
                    type="range" min="15" max="80" step="5"
                    value={maxClips}
                    onChange={(e) => setMaxClips(parseInt(e.target.value, 10))}
                    className="w-full accent-[#B89762]"
                    data-testid="max-clips-slider"
                  />
                  <p className="text-[10px] text-gray-600 mt-1">Estimated film length: ~{Math.round(maxClips * photoDuration / 60 * 10) / 10} min</p>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={handleGenerate}
            disabled={generating}
            className="w-full py-4 rounded-xl font-bold text-white text-lg disabled:opacity-50 hover:scale-[1.02] transition-all"
            style={{ background: 'linear-gradient(135deg, #B89762 0%, #A033D8 50%, #C29D52 100%)' }}
            data-testid="generate-film-btn"
          >
            {generating ? 'Starting...' : 'Create Our Event Film'}
          </button>
          <p className="text-center text-gray-600 text-xs mt-3">Only takes 3–10 minutes to generate</p>

          {/* SMART UPSELL — only show for non-priority events (free queue) */}
          {!event?.priority_render && (
            <div className="mt-3 rounded-xl p-3 flex items-start gap-3 border" style={{ borderColor: 'rgba(255,204,0,0.25)', background: 'linear-gradient(135deg, rgba(255,107,107,0.08), rgba(255,204,0,0.05))' }} data-testid="priority-upsell-banner">
              <div className="text-lg leading-none flex-shrink-0">⚡</div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-white">Skip the queue — £4.99</p>
                <p className="text-[11px] text-gray-400 leading-snug mt-0.5">Your film starts immediately, ready in seconds. No waiting behind other events.</p>
              </div>
              <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('session_token');
                    const r = await axios.post(`${API}/events/${event.event_id}/purchase-addon/instant_film`, {}, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (r.data?.url) window.location.href = r.data.url;
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Could not start checkout');
                  }
                }}
                className="px-3 py-1.5 rounded-full text-[11px] font-bold text-black flex-shrink-0"
                style={{ background: 'linear-gradient(135deg, #FF6B6B, #FFCC00)' }}
                data-testid="priority-upsell-btn">
                Fast track
              </button>
            </div>
          )}
        </div>
        {shareModalEl}
      </div>
    );
  }

  // ── STATE 3: PROCESSING ──
  if (status === 'processing') {
    const progress = filmStatus?.progress || 0;
    const stage = filmStatus?.stage || 'queued';
    return (
      <div className="rounded-2xl mb-8 overflow-hidden" style={{ background: '#0F0F10' }} data-testid="film-module-processing">
        <div className="p-8">
          <div className="flex items-center gap-2 mb-3">
            <Sparkle size={24} weight="fill" className="text-[#B89762] animate-pulse" />
            <h3 className="font-serif text-2xl font-bold text-white">Creating your film...</h3>
          </div>
          <p className="text-gray-400 text-sm mb-6">This usually takes 3-10 minutes. You can leave this page.</p>

          <div className="w-full h-2.5 bg-gray-800 rounded-full mb-4 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-1000"
              style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #B89762 0%, #A033D8 50%, #C29D52 100%)' }}
              data-testid="film-progress-bar"
            />
          </div>
          <p className="text-right text-xs text-gray-500 mb-4">{progress}%</p>

          <div className="space-y-2">
            {Object.entries(STAGE_LABELS).map(([key, label]) => {
              const stageOrder = Object.keys(STAGE_LABELS);
              const currentIdx = stageOrder.indexOf(stage);
              const thisIdx = stageOrder.indexOf(key);
              const isDone = thisIdx < currentIdx && currentIdx >= 0;
              const isCurrent = key === stage;
              return (
                <div key={key} className={`flex items-center gap-2 text-sm ${isDone ? 'text-[#B89762]' : isCurrent ? 'text-white' : 'text-gray-600'}`}>
                  <span>{isDone ? '✓' : isCurrent ? '⏳' : '•'}</span>
                  <span>{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── STATE 4: READY ──
  if (status === 'ready') {
    return (
      <div className="rounded-2xl mb-8 overflow-hidden" style={{ background: '#0F0F10' }} data-testid="film-module-ready">
        <div className="p-8">
          <div className="flex items-center gap-2 mb-4">
            <FilmStrip size={24} weight="bold" className="text-white" />
            <h3 className="font-serif text-2xl font-bold text-white">Your Event Film</h3>
            {filmStatus?.duration && <span className="text-gray-500 text-sm ml-auto" data-testid="film-duration">{filmStatus.duration}</span>}
          </div>

          <div className="relative rounded-xl overflow-hidden mb-5 bg-black aspect-[9/16] max-h-[70vh] mx-auto" style={{ maxWidth: 360 }}>
            <video
              ref={videoRef}
              src={getVideoUrl()}
              controls
              playsInline
              className="w-full h-full object-contain"
              data-testid="film-video-player"
            />
          </div>

          {/* Action buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            <button
              onClick={() => videoRef.current?.play()}
              className="py-3 rounded-xl font-semibold text-white text-sm hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              style={{ background: 'linear-gradient(135deg, #B89762, #A033D8)' }}
              data-testid="play-film-btn"
            >
              <Play size={18} weight="fill" /> Watch
            </button>
            <button
              onClick={handleDownload}
              className="py-3 rounded-xl border border-gray-600 text-white font-semibold text-sm hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
              data-testid="download-film-btn"
            >
              <DownloadSimple size={18} weight="bold" /> Download
            </button>
            <button
              onClick={handleCreateShare}
              disabled={creatingShare}
              className="py-3 rounded-xl border border-gray-600 text-white font-semibold text-sm hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="share-film-btn"
            >
              <ShareNetwork size={18} weight="bold" /> {creatingShare ? '…' : 'Share'}
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="py-3 rounded-xl border border-gray-600 text-white font-semibold text-sm hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="regenerate-film-btn"
              title="Regenerate with different settings"
            >
              <ArrowsClockwise size={18} weight="bold" /> Regenerate
            </button>
          </div>

          {/* Premium upsell */}
          {!event.film_premium && (
            <div className="rounded-xl p-5 border border-[#B89762]/50/20" style={{ background: 'linear-gradient(135deg, rgba(184,151,98,0.08), rgba(255,77,141,0.05))' }} data-testid="premium-upsell">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-bold text-[#B89762]">Upgrade to Premium</span>
                <span className="px-2 py-0.5 rounded-full bg-[#B89762]/20 text-[#C29D52] text-[10px] font-bold">CINEMATIC</span>
              </div>
              <p className="text-white/40 text-xs mb-3">Voice narration, enhanced storytelling, premium transitions, no watermark</p>
              <button
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API}/events/${event.event_id}/film/upgrade-premium`, {});
                    if (res.data.url) window.location.href = res.data.url;
                  } catch (e) { toast.error(e.response?.data?.detail || 'Upgrade failed'); }
                }}
                className="w-full py-2.5 rounded-lg font-bold text-white text-sm transition-all hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg, #B89762, #A855F7)' }}
                data-testid="upgrade-premium-btn"
              >
                Upgrade — £30
              </button>
            </div>
          )}
        </div>
        {shareModalEl}
      </div>
    );
  }

  return null;
};

export default FilmModule;
